import { useState, type ReactNode } from 'react'
import { ExerciseNameInput } from './ExerciseNameInput'
import { MAX_EXERCISES_PER_ITEM, type ItemRow } from './blockItemHelpers'
import { useTheme } from '../../hooks/useTheme'
import { DashedAddButton } from '../ui'

interface BlockItemsEditorProps {
  items: ItemRow[]
  onChange: (items: ItemRow[]) => void
  textColor?: string
  bgColor?: string
  labels: {
    itemsLabel: string
    addExercise: string
    addToGroup: string
    notePlaceholder: string
    addNote: string
    changeNote: string
    doneNote: string
    exerciseNamePlaceholder: string
    moveUp?: string
    moveDown?: string
    groupLabel?: string
    ungroup?: string
  }
  allowReorder?: boolean
}

export function BlockItemsEditor({
  items,
  onChange,
  textColor,
  bgColor,
  labels,
  allowReorder = false,
}: BlockItemsEditorProps) {
  const theme = useTheme()
  const fg = textColor ?? theme.text.primary
  const muted = theme.text.muted
  const accent = theme.linkColor || theme.accent.text

  function updateItem(index: number, patch: Partial<ItemRow>) {
    onChange(items.map((item, i) => (i === index ? { ...item, ...patch } : item)))
  }

  function updateName(itemIndex: number, nameIndex: number, value: string) {
    onChange(items.map((item, i) => {
      if (i !== itemIndex) return item
      const names = item.names.map((n, j) => (j === nameIndex ? value : n))
      return { ...item, names }
    }))
  }

  function addStandalone() {
    onChange([...items, { note: '', names: [''] }])
  }

  function addToGroup(itemIndex: number) {
    onChange(items.map((item, i) => {
      if (i !== itemIndex) return item
      if (item.names.length >= MAX_EXERCISES_PER_ITEM) return item
      return { ...item, names: [...item.names, ''] }
    }))
  }

  function removeName(itemIndex: number, nameIndex: number) {
    const next = items
      .map((item, i) => {
        if (i !== itemIndex) return item
        const names = item.names.filter((_, j) => j !== nameIndex)
        return { ...item, names }
      })
      .filter(item => item.names.length > 0)
    onChange(next.length > 0 ? next : [{ note: '', names: [''] }])
  }

  function removeItem(itemIndex: number) {
    const next = items.filter((_, i) => i !== itemIndex)
    onChange(next.length > 0 ? next : [{ note: '', names: [''] }])
  }

  function ungroup(itemIndex: number) {
    const item = items[itemIndex]
    if (!item || item.names.length <= 1) return
    const split: ItemRow[] = item.names.map(name => ({ note: '', names: [name] }))
    const next = [...items]
    next.splice(itemIndex, 1, ...split)
    onChange(next)
  }

  function moveItem(index: number, delta: number) {
    const target = index + delta
    if (target < 0 || target >= items.length) return
    const next = [...items]
    const [row] = next.splice(index, 1)
    next.splice(target, 0, row)
    onChange(next)
  }

  return (
    <div>
      <label className="block text-sm font-semibold mb-3" style={{ color: fg }}>
        {labels.itemsLabel}
      </label>

      <div className="flex flex-col gap-4">
        {items.map((item, itemIndex) => {
          const isGroup = item.names.length > 1
          return (
            <div
              key={itemIndex}
              className="flex flex-col gap-2"
              style={{
                paddingBottom: itemIndex < items.length - 1 ? 16 : 0,
                borderBottom:
                  itemIndex < items.length - 1 ? `1px solid ${theme.cardBorder}` : undefined,
              }}
            >
              <div className="flex items-center gap-2">
                <span
                  className="text-sm font-bold tabular-nums shrink-0"
                  style={{ color: muted, minWidth: 20 }}
                >
                  {itemIndex + 1}.
                </span>
                {isGroup && (
                  <span
                    className="text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-lg"
                    style={{
                      color: accent,
                      backgroundColor: `${theme.buttonColor}12`,
                    }}
                  >
                    {labels.groupLabel ?? 'Group'}
                  </span>
                )}
                <div className="flex items-center ml-auto">
                  {allowReorder && (
                    <>
                      <button
                        type="button"
                        onClick={() => moveItem(itemIndex, -1)}
                        disabled={itemIndex === 0}
                        aria-label={labels.moveUp}
                        className="p-2.5 rounded-xl disabled:opacity-20 transition-opacity"
                        style={{ color: muted }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="18 15 12 9 6 15"/>
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={() => moveItem(itemIndex, 1)}
                        disabled={itemIndex === items.length - 1}
                        aria-label={labels.moveDown}
                        className="p-2.5 rounded-xl disabled:opacity-20 transition-opacity"
                        style={{ color: muted }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="6 9 12 15 18 9"/>
                        </svg>
                      </button>
                    </>
                  )}
                  {items.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeItem(itemIndex)}
                      aria-label="Remove"
                      className="p-2.5 rounded-xl"
                      style={{ color: '#ef4444' }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"/>
                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                        <path d="M10 11v6"/><path d="M14 11v6"/>
                        <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                      </svg>
                    </button>
                  )}
                </div>
              </div>

              {item.names.map((name, nameIndex) => (
                <div key={nameIndex} className="flex items-start gap-2 pl-0 sm:pl-6">
                  {isGroup && (
                    <span
                      className="mt-2.5 text-xs font-bold shrink-0 w-4 text-center"
                      style={{ color: accent }}
                    >
                      {String.fromCharCode(65 + nameIndex)}
                    </span>
                  )}
                  <ExerciseNameInput
                    value={name}
                    onChange={value => updateName(itemIndex, nameIndex, value)}
                    placeholder={labels.exerciseNamePlaceholder}
                    textColor={fg}
                    bgColor={bgColor ?? theme.inputBg}
                    multiline
                  />
                  {item.names.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeName(itemIndex, nameIndex)}
                      className="mt-1 w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                      style={{ color: '#ef4444' }}
                      aria-label="Remove from group"
                    >
                      −
                    </button>
                  )}
                </div>
              ))}

              <ItemActions
                note={item.note}
                onNoteChange={note => updateItem(itemIndex, { note })}
                canAddToGroup={item.names.length < MAX_EXERCISES_PER_ITEM}
                isGroup={isGroup}
                labels={{
                  addToGroup: labels.addToGroup,
                  ungroup: labels.ungroup,
                  addNote: labels.addNote,
                  changeNote: labels.changeNote,
                  doneNote: labels.doneNote,
                  notePlaceholder: labels.notePlaceholder,
                }}
                onAddToGroup={() => addToGroup(itemIndex)}
                onUngroup={() => ungroup(itemIndex)}
                textColor={fg}
              />
            </div>
          )
        })}
      </div>

      <DashedAddButton className="mt-2" onClick={addStandalone}>
        {labels.addExercise}
      </DashedAddButton>
    </div>
  )
}

/** Soft accent chip — same language as TagSelector Add / program badges. */
function ActionChip({
  children,
  onClick,
  variant = 'accent',
}: {
  children: ReactNode
  onClick: () => void
  variant?: 'accent' | 'muted'
}) {
  const theme = useTheme()
  const isAccent = variant === 'accent'

  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 shrink-0 min-h-[40px] px-3 rounded-xl text-xs font-semibold transition-opacity active:opacity-70"
      style={
        isAccent
          ? {
              color: theme.accent.text,
              backgroundColor: `${theme.buttonColor}12`,
            }
          : {
              color: theme.text.muted,
              backgroundColor: theme.isDark ? 'rgba(255,255,255,0.06)' : theme.cardBg,
              border: `1px solid ${theme.cardBorder}`,
            }
      }
    >
      {children}
    </button>
  )
}

function ItemActions({
  note,
  onNoteChange,
  canAddToGroup,
  isGroup,
  labels,
  onAddToGroup,
  onUngroup,
  textColor,
}: {
  note: string
  onNoteChange: (value: string) => void
  canAddToGroup: boolean
  isGroup: boolean
  labels: {
    addToGroup: string
    ungroup?: string
    addNote: string
    changeNote: string
    doneNote: string
    notePlaceholder: string
  }
  onAddToGroup: () => void
  onUngroup: () => void
  textColor: string
}) {
  const theme = useTheme()
  const trimmed = note.trim()
  const [editingNote, setEditingNote] = useState(false)

  return (
    <div className="flex flex-col gap-2 pl-0 sm:pl-6">
      <div className="flex flex-wrap gap-2">
        {canAddToGroup && (
          <ActionChip onClick={onAddToGroup}>
            <PlusIcon />
            {labels.addToGroup}
          </ActionChip>
        )}
        {!editingNote && !trimmed && (
          <ActionChip onClick={() => setEditingNote(true)}>
            <NoteIcon />
            {labels.addNote}
          </ActionChip>
        )}
        {isGroup && labels.ungroup && (
          <ActionChip variant="muted" onClick={onUngroup}>
            {labels.ungroup}
          </ActionChip>
        )}
        {editingNote && (
          <ActionChip onClick={() => setEditingNote(false)}>
            {labels.doneNote}
          </ActionChip>
        )}
      </div>

      {!editingNote && trimmed && (
        <div
          className="rounded-xl px-3 py-2.5 flex flex-col gap-2"
          style={{
            backgroundColor: theme.inputBg,
            border: `1px solid ${theme.inputBorder}`,
          }}
        >
          <p
            className="text-sm leading-relaxed whitespace-pre-wrap"
            style={{ color: theme.text.secondary }}
          >
            {trimmed}
          </p>
          <div>
            <ActionChip onClick={() => setEditingNote(true)}>
              <NoteIcon />
              {labels.changeNote}
            </ActionChip>
          </div>
        </div>
      )}

      {editingNote && (
        <textarea
          value={note}
          onChange={e => onNoteChange(e.target.value)}
          placeholder={labels.notePlaceholder}
          rows={2}
          autoFocus
          className="w-full rounded-xl px-3 py-2.5 focus:outline-none text-sm resize-none leading-relaxed"
          style={{
            color: textColor,
            backgroundColor: theme.inputBg,
            border: `1px solid ${theme.inputBorder}`,
          }}
        />
      )}
    </div>
  )
}

function PlusIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden>
      <path d="M12 5v14M5 12h14" />
    </svg>
  )
}

function NoteIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="8" y1="13" x2="16" y2="13" />
      <line x1="8" y1="17" x2="13" y2="17" />
    </svg>
  )
}
